import type React from "react";
import { Fragment, useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { HealthReport } from "api/models/HealthReport";
import { useLocalizedDate } from "utils/date";

import type { PainLocation, Question } from "../../helpers";
import { answersChanged, someAnswered } from "../../helpers";
import { useColumnConfiguration } from "../hooks";

import { AnswerCell } from "./AnswerCell";
import { GridHeader } from "./GridHeader";
import { QuestionCell } from "./QuestionCell";
import { GridContainer } from "./styles";

interface HealthReportsGridProps {
  selectedHealthReports: HealthReport[];
  questions: Question[];
  hideUnmodifiedRows: boolean;
  painLocation?: PainLocation;
}

export const HealthReportsGrid: React.FC<HealthReportsGridProps> = ({
  selectedHealthReports,
  questions,
  hideUnmodifiedRows,
  painLocation,
}) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();

  const { columns, columnCount } = useColumnConfiguration(
    selectedHealthReports,
    (date: string) => format(parseISO(date), "P"),
    (reportNumber: number) => t("patients.health_reports.questionnaire", { number: reportNumber })
  );

  const shouldRenderQuestion = useMemo(
    () => (question: Question, parent?: Question) => {
      const isSub = !!parent;
      const parentKey = parent?.key || null;

      if (!someAnswered(selectedHealthReports, question.key, isSub, parentKey)) {
        return false;
      }

      if (hideUnmodifiedRows && !answersChanged(selectedHealthReports, question.key, isSub, parentKey, true)) {
        return false;
      }

      return true;
    },
    [selectedHealthReports, hideUnmodifiedRows]
  );
  const renderRows = () => {
    let rowIndex = 0;
    const rows: React.ReactNode[] = [];

    questions.forEach(question => {
      if (shouldRenderQuestion(question)) {
        const currentRowIndex = rowIndex;
        rowIndex += 1;
        rows.push(
          <Fragment key={question.key}>
            <QuestionCell question={question} isSubQuestion={false} rowIndex={currentRowIndex} />

            {columns.map(column => (
              <AnswerCell
                key={`${question.key}-${column.id}`}
                report={column.report}
                question={question}
                hasChanged={
                  selectedHealthReports.length > 1
                    ? answersChanged(selectedHealthReports, question.key, false, null, true)
                    : false
                }
                painLocation={painLocation}
                rowIndex={currentRowIndex}
              />
            ))}
          </Fragment>
        );
      }

      if (question.sub_questions) {
        question.sub_questions.forEach(subQuestion => {
          if (shouldRenderQuestion(subQuestion, question)) {
            const currentRowIndex = rowIndex;
            rowIndex += 1;
            rows.push(
              <Fragment key={subQuestion.key}>
                <QuestionCell question={subQuestion} isSubQuestion rowIndex={currentRowIndex} />

                {columns.map(column => (
                  <AnswerCell
                    key={`${subQuestion.key}-${column.id}`}
                    report={column.report}
                    question={subQuestion}
                    parent={question}
                    hasChanged={answersChanged(selectedHealthReports, subQuestion.key, true, question.key, true)}
                    painLocation={painLocation}
                    rowIndex={currentRowIndex}
                  />
                ))}
              </Fragment>
            );
          }
        });
      }
    });

    return rows;
  };

  return (
    <GridContainer columnCount={columnCount} data-testid="health-reports-grid">
      <GridHeader columns={columns} />

      {renderRows()}
    </GridContainer>
  );
};
